import {
  ReactFlow,
  applyNodeChanges,
  applyEdgeChanges,
  Background,
  Node,
  NodeChange,
  Connection,
  Edge,
  Controls,
  MiniMap,
  useReactFlow,
  EdgeChange,
  ConnectionLineType,
  CoordinateExtent,
  useUpdateNodeInternals,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useState, useMemo, useEffect } from 'react';

import './dataset_common.css';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import CanvasTable from './CanvasTable';
import RelationshipDefiningComponent from '../DataSet/RelationshipDefiningComponent';
import { IArrow, IColumn, IRelationship, ISelectedTable } from '../../redux/_Dataset/types';
import { useDispatch } from 'react-redux';
import { TAlert } from '../../types/types';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import { AddArrow, SetSelectedTables } from '../../redux/_Dataset/dataset.action';
import ShortUniqueId from 'short-unique-id';
import { ArrowObj } from '../DataSet/types/CanvasInterfaces';
import { useLocation } from 'react-router-dom';
import RelationshipEdge from './RelationshipEdge';
import { calculateNewNodePosition, reverseCardinality, reverseIntegrity } from './utils';

interface ICanvas {
  onTableUncheck: (tableId: string[]) => void;
  datasetFilterWidth: number;
  sidebarWidth: number;
  showSidebar: boolean;
  singleSidebar: boolean;
  invalidTables?: Set<string>;
}
type ArrowWithExtras = IArrow & {
  colType?: string;
};

type RelationshipInitArgs = {
  sourceColumn: IColumn;
  targetColumn: IColumn;
  sourceTable: ISelectedTable;
  targetTable: ISelectedTable;
  arrows: IArrow[];
};

export type TableNodeData = {
  table: ISelectedTable;
  onTableUncheck: (tableId: string[]) => void;
  isConnecting: boolean;
  isInvalid?: boolean;
};
export type TableNode = Node<TableNodeData, 'tableNode'>;
const isSameOrDuplicate = (sourceTable: ISelectedTable, targetTable: ISelectedTable) => {
  if (sourceTable.id === targetTable.id) return true;
  else if (sourceTable.table === targetTable.table) {
    /**if both flatfile */
    if (sourceTable.isFlatFile && targetTable.isFlatFile)
      sourceTable.flatFileId === targetTable.flatFileId;
    /**if both from dbcon */ else if (sourceTable.connectionId && targetTable.connectionId) {
      if (sourceTable.connectionId !== targetTable.connectionId) return false;
      const sqlVendors = ['mysql', 'sqlserver', 'clickhouse'];
      /** if connectionid is same then vendor will be same */
      if (sqlVendors.includes(sourceTable.vendor)) sourceTable.database === targetTable.database;
      return (
        sourceTable.database === targetTable.database && sourceTable.schema === targetTable.schema
      );
    }
  }
  return false;
};
const initiateRelationshipCreation = ({
  sourceColumn,
  targetColumn,
  sourceTable,
  targetTable,
  arrows,
}: RelationshipInitArgs): { error?: string; arrowObj?: ArrowObj } => {
  // 1. Same table?
  if (isSameOrDuplicate(sourceTable, targetTable)) {
    return { error: 'Can not create relation between same table or its copies' };
  }

  // 3. Matching data types?
  if (sourceColumn.dataType !== targetColumn.dataType) {
    return { error: 'Relationship can only build with same data types.' };
  }

  // 4. Already related?
  const existingRel = arrows.find(
    (arr) =>
      (arr.start === sourceColumn.uid && arr.end === targetColumn.uid) ||
      (arr.end === sourceColumn.uid && arr.start === targetColumn.uid),
  );
  if (existingRel) {
    return { error: 'There is already an existing relation between these two columns' };
  }

  // Build ArrowObj
  const arrowObj: ArrowObj = {
    isSelected: true,
    startTableName: sourceTable.table,
    startColumnName: sourceColumn.columnName,
    start: sourceColumn.uid,
    table1_uid: sourceTable.id,
    startSchema: sourceTable.schema ?? '',
    startId: sourceTable.id,
    endTableName: targetTable.table,
    endColumnName: targetColumn.columnName,
    end: targetColumn.uid,
    table2_uid: targetTable.id,
    endSchema: targetTable.schema ?? '',
    endId: targetTable.id,
  };

  return { arrowObj };
};

function Canvas({
  onTableUncheck,
  datasetFilterWidth,
  sidebarWidth,
  showSidebar,
  singleSidebar,
  invalidTables,
}: ICanvas) {
  const { selectedTables, arrows, canEdit } = useSelector((state: RootState) => ({
    selectedTables: state.dataset.selectedTables,
    arrows: state.dataset.arrows,
    canEdit: state.dataset.canEdit,
  }));
  const dispatch = useDispatch();
  const location = useLocation();
  const [existingArrowProp, setExistingArrowProp] = useState<ArrowWithExtras | null>(null);
  const [existingArrow, setExistingArrow] = useState<boolean>(false);
  const [arrowHovered, setArrowHovered] = useState<string | null>(null);
  const [alert, setAlert] = useState<TAlert | null>(null);
  const uid = new ShortUniqueId({ length: 8 });
  const [arrowProp, setArrowProp] = useState<IArrow | null>(null);
  const [relationshipDialog, setShowRelationshipDialog] = useState(false);
  const [nodes, setNodes] = useState<Node<TableNodeData>[]>([]);
  const [edges, setEdges] = useState<any[]>([]);
  const updateNodeInternals = useUpdateNodeInternals();
  const { fitView } = useReactFlow();
  const [isConnecting, setIsConnecting] = useState<boolean>(false);

  const canvasWidth = singleSidebar
    ? `calc(100vw - ${sidebarWidth}rem)`
    : showSidebar && selectedTables?.length > 0
      ? `calc(100vw - ${sidebarWidth}rem - 15rem - ${datasetFilterWidth}rem)`
      : showSidebar
        ? `calc(100vw - ${sidebarWidth}rem - 15rem)`
        : `calc(100vw - ${sidebarWidth}rem - ${datasetFilterWidth}rem)`;

  const onPermitForRelationCreate = (arrow: IArrow) => {
    const savedTableIds = new Set(selectedTables.filter((t) => !t.isNewTable).map((t) => t.id));

    // if (savedTableIds.has(arrow.startId) && savedTableIds.has(arrow.endId)) {
    //   setAlert({
    //     message: 'Cannot add or edit relation between already saved tables.',
    //     severity: 'error',
    //   });
    //   return;
    // }

    let alreadyRelated: IRelationship | null = null;
    for (const existing of arrows) {
      if (
        [existing.startId, existing.endId].includes(arrow.startId) &&
        [existing.startId, existing.endId].includes(arrow.endId)
      ) {
        alreadyRelated = existing;
        break;
      }
    }

    if (alreadyRelated) {
      const isOppositeDir =
        alreadyRelated.startId === arrow.endId && alreadyRelated.endId === arrow.startId;

      const newArrowObj = {
        ...arrow,
        arrowId: uid(),
        relationId: alreadyRelated.relationId,
        isSelected: false,
        integrity: isOppositeDir
          ? reverseIntegrity(alreadyRelated.integrity)
          : alreadyRelated.integrity,
        cardinality: isOppositeDir
          ? reverseCardinality(alreadyRelated.cardinality)
          : alreadyRelated.cardinality,
        showHead: alreadyRelated.showHead,
        showTail: alreadyRelated.showTail,
      };
      dispatch(AddArrow(newArrowObj));
    } else {
      arrow.relationId = uid();
      arrow.arrowId = uid();
      setArrowProp(arrow);
      setShowRelationshipDialog(true);
    }
  };

  const safeInvalidTables = useMemo(() => invalidTables ?? new Set<string>(), [invalidTables]);

  const computedNodes: Node<TableNodeData>[] = useMemo(() => {
    return selectedTables.map((table, idx) => ({
      id: table.id,
      position: {
        x: table?.tablePositionX ? table.tablePositionX : calculateNewNodePosition(idx)?.x,
        y: table?.tablePositionY ? table.tablePositionY : calculateNewNodePosition(idx)?.y,
      },
      zIndex: table?.order ?? idx + 1,
      type: 'tableNode',
      data: {
        table,
        onTableUncheck,
        isConnecting,
        isInvalid: safeInvalidTables.has(table.id),
      },
      dragHandle: '.draggableBoxTitle',
    }));
  }, [selectedTables, onTableUncheck, isConnecting, safeInvalidTables]);

  const computedEdges = useMemo(() => {
    return arrows
      .map((arrow) => {
        const startNode = nodes.find((node) => node.id === arrow.startId);
        const endNode = nodes.find((node) => node.id === arrow.endId);

        if (!startNode || !endNode) return null;

        const startX = startNode.position.x;
        const endX = endNode.position.x;

        const startWidth = startNode?.measured?.width ?? 200;
        const endWidth = endNode?.measured?.width ?? 200;

        const startLeft = startX;
        const startRight = startX + startWidth;

        const endLeft = endX;
        const endRight = endX + endWidth;

        const distances = [
          { source: 'left', target: 'left', dist: Math.abs(startLeft - endLeft) },
          { source: 'right', target: 'right', dist: Math.abs(startRight - endRight) },
          { source: 'left', target: 'right', dist: Math.abs(startLeft - endRight) },
          { source: 'right', target: 'left', dist: Math.abs(startRight - endLeft) },
        ];

        const best = distances.reduce((prev, curr) => (curr.dist < prev.dist ? curr : prev));

        return {
          id: arrow.arrowId,
          source: arrow.startId,
          target: arrow.endId,
          sourceHandle: `${arrow.start}-source-${best.source}`,
          targetHandle: `${arrow.end}-target-${best.target}`,
          type: 'relationship',
          style: { stroke: arrowHovered === arrow.arrowId ? '#404040' : '#9ca3af', strokeWidth: 2 },
          data: { arrow, arrowHovered },
        };
      })
      .filter(Boolean);
  }, [arrows, arrowHovered, nodes]);

  const nodeTypes = useMemo(
    () => ({
      tableNode: CanvasTable,
    }),
    [],
  );

  const edgeTypes = useMemo(
    () => ({
      relationship: RelationshipEdge,
    }),
    [],
  );

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds) as Node<TableNodeData>[]);
  }, []);

  const onNodeDragStart = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const tableOrders: number[] =
        selectedTables?.map((table: ISelectedTable) => Number(table?.order ?? 0)) ?? [];
      const maxTableOrder = tableOrders?.length > 0 ? Math.max(...tableOrders) : 0;

      dispatch(
        SetSelectedTables(
          selectedTables.map((t) =>
            t.id === node.id
              ? {
                  ...t,
                  order: maxTableOrder + 1,
                }
              : t,
          ),
        ),
      );
    },
    [dispatch, selectedTables],
  );

  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      dispatch(
        SetSelectedTables(
          selectedTables.map((t) =>
            t.id === node.id
              ? {
                  ...t,
                  tablePositionX: node.position.x,
                  tablePositionY: node.position.y,
                }
              : t,
          ),
        ),
      );
      updateNodeInternals(node.id);
    },
    [dispatch, selectedTables, updateNodeInternals],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) =>
      setEdges((edgesSnapshot) => applyEdgeChanges(changes, edgesSnapshot)),
    [],
  );

  const onConnect = useCallback(
    (params: Connection) => {
      const sourceColUid = params.sourceHandle?.split('-')[0];
      const targetColUid = params.targetHandle?.split('-')[0];

      if (!sourceColUid || !targetColUid) return;

      const sourceTable = selectedTables.find((t) => t.columns.some((c) => c.uid === sourceColUid));
      const targetTable = selectedTables.find((t) => t.columns.some((c) => c.uid === targetColUid));
      if (!sourceTable || !targetTable) return;

      const sourceColumn = sourceTable.columns.find((c) => c.uid === sourceColUid)!;
      const targetColumn = targetTable.columns.find((c) => c.uid === targetColUid)!;

      const { error, arrowObj } = initiateRelationshipCreation({
        sourceColumn,
        targetColumn,
        sourceTable,
        targetTable,
        arrows,
      });

      if (error) {
        setAlert({
          message: error,
          severity: 'warning',
        });
        return;
      }

      onPermitForRelationCreate(arrowObj as IArrow);
    },
    [selectedTables, arrows],
  );

  const onEdgeMouseEnter = (_: any, edge: any) => setArrowHovered(edge.id);
  const onEdgeMouseLeave = () => setArrowHovered(null);

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    const arrowData = edge.data?.arrow as IArrow;
    if (arrowData) {
      setExistingArrowProp({ ...arrowData });
      setExistingArrow(true);
      setShowRelationshipDialog(true);
    }
  }, []);

  // effects
  useEffect(() => {
    setEdges(computedEdges);
  }, [computedEdges]);
  useEffect(() => {
    setNodes(computedNodes);
  }, [computedNodes]);

  useEffect(() => {
    if (location.pathname.startsWith('/editdataset/')) {
      fitView();
    }
  }, []);

  useEffect(() => {
    fitView();
  }, [selectedTables?.length]);

  // for preventing the infinite drag in the canvas
  const nodeBounds: CoordinateExtent = [
    [-1900, -1000], //minX and minY
    [1950, 1110], //maxX and maxY
  ];

  const translateBounds: CoordinateExtent = [
    [-1930, -1020], // extra space for camera movement
    [1980, 1130],
  ];

  return (
    <Box
      id="canvasTableArea"
      className="canvasStyle"
      sx={{
        width: canvasWidth,
        height: '100%',
        position: 'relative',
        borderTop: `0px !important`,
        display: 'inline-block',
      }}
    >
      <div id="canvasStage" className="canvasStage">
        <ReactFlow
          className={isConnecting ? 'rf-connecting' : ''}
          minZoom={0.3}
          maxZoom={1.5}
          nodes={nodes}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onConnectStart={() => setIsConnecting(true)}
          onConnectEnd={() => setIsConnecting(false)}
          onEdgeClick={onEdgeClick}
          onEdgeMouseEnter={onEdgeMouseEnter}
          onEdgeMouseLeave={onEdgeMouseLeave}
          proOptions={{ hideAttribution: true }}
          onNodeDragStart={onNodeDragStart}
          onNodeDragStop={onNodeDragStop}
          connectionLineStyle={{
            stroke: '#9ca3af',
            strokeWidth: 2,
          }}
          connectionLineType={ConnectionLineType.SmoothStep}
          nodeExtent={nodeBounds}
          translateExtent={translateBounds}
          fitViewOptions={{ padding: 0.2 }}
        >
          <Controls />
          <MiniMap
            pannable
            zoomable
            style={{
              width: 120,
              height: nodes.length === 0 ? 0 : 100,
            }}
          />
          <Background />
        </ReactFlow>
      </div>
      {!arrowProp && !existingArrowProp ? null : (
        <RelationshipDefiningComponent
          id="idarrow"
          showRelationCard={relationshipDialog}
          setShowRelationCard={setShowRelationshipDialog}
          existingArrowProp={existingArrowProp}
          existingArrow={existingArrow}
          arrowProp={arrowProp}
          setArrowProp={setArrowProp}
          setExistingArrow={setExistingArrow}
          setExistingArrowProp={setExistingArrowProp}
          canEdit={canEdit}
        />
      )}
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert.message}
          openAlert={!!alert}
        />
      )}
    </Box>
  );
}
export default Canvas;
